<template>
  <teleport to="body">
    <div
      v-if="visible"
      class="modal fade show d-block cfdi-preview-modal-overlay"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      @click.self="$emit('close')"
    >
      <div class="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title cfdi-prev-modal-title">
              <i class="fa fa-file-text-o me-2"></i>{{ uuid || 'Detalle CFDI' }}
            </h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="$emit('close')"></button>
          </div>
          <div class="modal-body">
            <div v-if="loading" class="text-center py-4">
              <i class="fa fa-spinner fa-spin me-2"></i> Cargando detalle...
            </div>
            <div v-else-if="error" class="alert alert-danger mb-0">{{ error }}</div>
            <CfdiPreview
              v-else-if="previewData"
              :cfdi="previewData.cfdi"
              :emisor="previewData.emisor"
              :receptor="previewData.receptor"
              :conceptos="previewData.conceptos"
              :totales="previewData.totales"
              :nomina="previewData.nomina"
              :relatedData="relatedData"
              :relationType="relationType"
              :cfdisRelacionados="cfdisRelacionados"
              :documentosRelacionados="documentosRelacionados"
              :navigationSource="navigationSource"
              :showStatusRefresh="true"
              :refreshingStatus="requestingStatusRefresh"
              :statusRefreshDisabled="statusRefreshBlocked"
              :statusRefreshTitle="statusRefreshTitle"
              @request-status-refresh="requestStatusRefresh"
            />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-auto" @click="$emit('close')">Cerrar Ventana</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canDownload || downloadingXml"
              @click="downloadXml"
            >
              <i v-if="downloadingXml" class="fa fa-spinner fa-spin me-1"></i>
              XML
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="!canDownload || downloadingPdf"
              @click="downloadPdf"
            >
              <i v-if="downloadingPdf" class="fa fa-spinner fa-spin me-1"></i>
              PDF
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="visible" class="modal-backdrop fade show cfdi-preview-backdrop"></div>
  </teleport>
</template>

<script>
import CfdiPreview from '@/components/cfdis/CfdiPreview.vue';
import cfdisService from '@/services/cfdis';
import dashboardWs from '@/services/dashboard';

export default {
  name: 'CfdiPreviewModal',
  components: { CfdiPreview },
  emits: ['close'],
  props: {
    visible:     { type: Boolean, default: false },
    uuid:        { type: String,  default: ''    },
    rfc:         { type: String,  default: ''    },
    sourceItem:  { type: Object,  default: null  },
    fieldCode:   { type: String,  default: ''    },
  },
  data() {
    return {
      loading:                false,
      error:                  '',
      previewData:            null,
      relatedData:            null,
      relationType:           '', // 'payment' o 'relation'
      cfdisRelacionados:      null,
      documentosRelacionados: null,
      navigationSource:       'direct', // 'direct', 'documento_relacionado', 'cfdi_relacionado'
      downloadingXml:         false,
      downloadingPdf:         false,
      requestingStatusRefresh:false,
      statusRefreshBlocked:   false,
      statusRefreshTitle:     'Solicitar actualización SAT',
    };
  },
  computed: {
    canDownload() {
      return !!(this.previewData && this.uuid && !this.loading && !this.error);
    },
  },
  watch: {
    visible(val) {
      if (val && this.uuid) this.fetchDetail();
      else this.reset();
    },
    uuid(val) {
      if (val && this.visible) this.fetchDetail();
    },
  },
  methods: {
    reset() {
      this.loading                = false;
      this.error                  = '';
      this.previewData            = null;
      this.relatedData            = null;
      this.relationType           = '';
      this.cfdisRelacionados      = null;
      this.documentosRelacionados = null;
      this.navigationSource       = 'direct';
      this.downloadingXml         = false;
      this.downloadingPdf         = false;
      this.requestingStatusRefresh = false;
      this.statusRefreshBlocked   = false;
      this.statusRefreshTitle     = 'Solicitar actualización SAT';
    },
    extractPayload(response) {
      const isRaw = !!(response && typeof response === 'object' && 'status' in response && 'config' in response);
      return isRaw ? response.data : response;
    },
    async fetchDetail() {
      this.loading                = true;
      this.error                  = '';
      this.previewData            = null;
      this.relatedData            = null;
      this.relationType           = '';
      this.cfdisRelacionados      = null;
      this.documentosRelacionados = null;

      // Determinar navigationSource basado en fieldCode
      if (this.fieldCode === 'documentos_relacionados') {
        this.navigationSource = 'documento_relacionado';
      } else if (this.fieldCode === 'cfdis_relacionados') {
        this.navigationSource = 'cfdi_relacionado';
      } else {
        this.navigationSource = 'direct';
      }

      const rfc = this.rfc || localStorage.getItem('rfc') || '';

      try {
        const response = await cfdisService.getCfdiDetail(rfc, this.uuid);
        const payload  = this.extractPayload(response);

        if (payload?.type === 'success' && payload?.data) {
          this.previewData = payload.data;
          await this.fetchStatusRefreshAvailability();
          
          // Cargar datos relacionados de forma contextual e inversa.
          if (this.fieldCode === 'documentos_relacionados' && this.sourceItem?.id) {
            await Promise.all([
              this.fetchRelatedData(),
              this.fetchCfdisRelacionados(),
            ]);
          } else if (this.fieldCode === 'cfdis_relacionados' && this.sourceItem?.id) {
            await Promise.all([
              this.fetchRelatedData(),
              this.fetchDocumentosRelacionados(),
            ]);
          } else {
            await Promise.all([
              this.fetchCfdisRelacionados(),
              this.fetchDocumentosRelacionados(),
            ]);
          }
        } else {
          this.error = payload?.message || 'No se pudo cargar el detalle del CFDI.';
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'No se pudo cargar el detalle del CFDI.';
      } finally {
        this.loading = false;
      }
    },
    async fetchStatusRefreshAvailability() {
      const uuid = this.currentUuid();
      const rfc = this.currentRfc();

      if (!uuid || !rfc) {
        this.statusRefreshBlocked = true;
        this.statusRefreshTitle = 'No disponible';
        return;
      }

      try {
        const response = await cfdisService.getCfdiStatusRefreshState(rfc, uuid);
        const payload = this.extractPayload(response);
        const hasPending = !!payload?.data?.has_pending_request;

        this.statusRefreshBlocked = false;
        this.statusRefreshTitle = hasPending
          ? 'Validar estatus SAT ahora (ya existe una solicitud diferida pendiente)'
          : 'Validar estatus SAT ahora';
      } catch (e) {
        // Si no se puede validar disponibilidad, permitimos intentar validación directa.
        this.statusRefreshBlocked = false;
        this.statusRefreshTitle = 'Validar estatus SAT ahora';
      }
    },
    async fetchRelatedData() {
      try {
        const rfc = this.rfc || localStorage.getItem('rfc') || '';
        
        if (this.fieldCode === 'documentos_relacionados') {
          // Cargar detalles del pago
          const response = await cfdisService.getPaymentRelatedDetail(rfc, this.sourceItem.id, this.uuid);
          const payload = this.extractPayload(response);
          
          if (payload?.type === 'success' && payload?.data) {
            this.relatedData = payload.data;
            this.relationType = 'payment';
          }
        } else if (this.fieldCode === 'cfdis_relacionados') {
          // Cargar detalles de la relación
          const response = await cfdisService.getCfdiRelationDetail(rfc, this.sourceItem.id, this.uuid);
          const payload = this.extractPayload(response);
          
          if (payload?.type === 'success' && payload?.data) {
            this.relatedData = payload.data;
            this.relationType = 'relation';
          }
        }
      } catch (e) {
        // Si falla cargar datos relacionados, no mostramos error, solo omitimos esa sección
        console.warn('No se pudo cargar datos relacionados:', e);
      }
    },
    async fetchCfdisRelacionados() {
      try {
        const rfc = this.rfc || localStorage.getItem('rfc') || '';
        const response = await cfdisService.getCfdiRelations(rfc, this.uuid);
        const payload = this.extractPayload(response);
        
        if (payload?.type === 'success' && payload?.data) {
          this.cfdisRelacionados = payload.data;
        }
      } catch (e) {
        console.warn('No se pudo cargar CFDIs relacionados:', e);
      }
    },
    async fetchDocumentosRelacionados() {
      try {
        const rfc = this.rfc || localStorage.getItem('rfc') || '';
        const response = await cfdisService.getCfdiPaymentRelations(rfc, this.uuid);
        const payload = this.extractPayload(response);
        
        if (payload?.type === 'success' && payload?.data) {
          this.documentosRelacionados = payload.data;
        }
      } catch (e) {
        console.warn('No se pudo cargar documentos relacionados:', e);
      }
    },
    currentUuid() {
      return String(this.uuid || '').trim().toUpperCase();
    },
    currentRfc() {
      return this.rfc || localStorage.getItem('rfc') || '';
    },
    triggerBlobDownload(blob, fileName) {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },
    async downloadXml() {
      const uuid = this.currentUuid();
      const rfc = this.currentRfc();

      if (!uuid || !rfc || this.downloadingXml) {
        return;
      }

      this.downloadingXml = true;

      try {
        const response = await dashboardWs.getDashboardCfdiXml(rfc, uuid, { skipLoading: true });
        const blob = response instanceof Blob
          ? response
          : new Blob([response], { type: 'application/xml;charset=utf-8' });

        this.triggerBlobDownload(blob, `${uuid}.xml`);
      } catch (error) {
        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error('No fue posible descargar el XML seleccionado.');
        }
      } finally {
        this.downloadingXml = false;
      }
    },
    async downloadPdf() {
      const uuid = this.currentUuid();
      const rfc = this.currentRfc();

      if (!uuid || !rfc || this.downloadingPdf) {
        return;
      }

      this.downloadingPdf = true;

      try {
        const response = await dashboardWs.getDashboardCfdiPdf(rfc, uuid, { skipLoading: true });
        const blob = response instanceof Blob
          ? response
          : new Blob([response], { type: 'application/pdf' });

        this.triggerBlobDownload(blob, `${uuid}.pdf`);
      } catch (error) {
        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error('No fue posible descargar el PDF seleccionado.');
        }
      } finally {
        this.downloadingPdf = false;
      }
    },
    async requestStatusRefresh() {
      const uuid = this.currentUuid();
      const rfc = this.currentRfc();

      if (!uuid || !rfc || this.requestingStatusRefresh || this.statusRefreshBlocked) {
        return;
      }

      this.requestingStatusRefresh = true;

      try {
        const directResponse = await cfdisService.validateCfdiStatusNow(rfc, uuid);
        const directPayload = this.extractPayload(directResponse);

        if (directPayload?.type === 'success' && directPayload?.data?.validated) {
          const newEstado = directPayload?.data?.status?.after?.estado;

          if (this.previewData?.cfdi && newEstado) {
            this.previewData = {
              ...this.previewData,
              cfdi: {
                ...this.previewData.cfdi,
                estado: newEstado,
              },
            };
          }

          this.statusRefreshTitle = 'Validar estatus SAT ahora';

          if (this.$toast && typeof this.$toast.success === 'function') {
            this.$toast.success(directPayload?.message || `Estatus SAT validado: ${newEstado || 'sin cambios'}`);
          }
          return;
        }

        // Fallback: no se pudo validar en linea, se programa la actualizacion diferida.
        const queuedResponse = await cfdisService.requestCfdiStatusRefresh(rfc, uuid);
        const queuedPayload = this.extractPayload(queuedResponse);

        if (queuedPayload?.type === 'success') {
          this.statusRefreshBlocked = true;
          this.statusRefreshTitle = 'Ya existe una solicitud pendiente para este periodo';
          if (this.$toast && typeof this.$toast.success === 'function') {
            this.$toast.success(queuedPayload?.message || 'Se solicitó la actualización diferida de estatus SAT.');
          }
          return;
        }

        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error(queuedPayload?.message || 'No fue posible validar ni solicitar actualización de estatus SAT.');
        }
      } catch (error) {
        try {
          const queuedResponse = await cfdisService.requestCfdiStatusRefresh(rfc, uuid);
          const queuedPayload = this.extractPayload(queuedResponse);

          if (queuedPayload?.type === 'success') {
            this.statusRefreshBlocked = true;
            this.statusRefreshTitle = 'Ya existe una solicitud pendiente para este periodo';
            if (this.$toast && typeof this.$toast.success === 'function') {
              this.$toast.success(queuedPayload?.message || 'Se solicitó la actualización diferida de estatus SAT.');
            }
            return;
          }
        } catch (queuedError) {
          if (queuedError?.response?.status === 409) {
            this.statusRefreshBlocked = true;
            this.statusRefreshTitle = 'Ya existe una solicitud pendiente para este periodo';
          }
        }

        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error(error?.response?.data?.message || 'No fue posible validar ni solicitar actualización de estatus SAT.');
        }
      } finally {
        this.requestingStatusRefresh = false;
      }
    },
  },
};
</script>

<style scoped>
.cfdi-preview-modal-overlay {
  z-index: 1060;
}

.cfdi-preview-backdrop {
  z-index: 1059;
}

.cfdi-prev-modal-title {
  font-size: 12px;
  font-family: monospace;
  word-break: break-all;
}
</style>
